import { Fragment, type HTMLAttributes } from 'react';

/**
 * Direction C — "The Rail", on-air side. player-web only.
 *
 * The broadcast crawl, carrying the player's own position rather than news. It is the one
 * element that never scrolls away, and it is what orients the player to their standing —
 * the mirror of the room's rundown.
 *
 * Rule 2 is enforced by construction: this component renders no controls at all. Orange in
 * player-web means "this is you", so `self` facts are the only orange figures on the strip,
 * its label and edge are orange only while `labelIsSelf` (#527), and nothing here is
 * clickable. It also states no figure the caller did not supply — there is no fallback, no
 * derived total, no "0" standing in for an unknown.
 */

export interface TickerFact {
  id: string;
  /** Short caption — e.g. "rank", "this week". Omit where the value speaks for itself. */
  label?: string;
  value: string;
  /** True when this fact is about the signed-in player, which is what earns it the accent. */
  self?: boolean;
}

export function Ticker({
  facts,
  label = 'You',
  labelIsSelf = true,
  className = '',
}: {
  facts: TickerFact[];
  /** What the strip is about. "You" on the reader's own portal; staff browsing name the player instead (#527). */
  label?: string;
  /** True when `label` is the signed-in player. Only then is the strip's label and edge signal orange. */
  labelIsSelf?: boolean;
  className?: string;
}) {
  if (facts.length === 0) return null;

  return (
    <div
      className={`flex items-stretch overflow-hidden border-t-2 bg-rail-900 ${labelIsSelf ? 'border-signal' : 'border-white/20'} ${className}`}
      aria-label={labelIsSelf ? 'Your position' : label}
    >
      <span
        className={`flex shrink-0 items-center px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-white ${labelIsSelf ? 'bg-signal' : 'bg-white/10'}`}
      >
        {label}
      </span>
      {/* The crawl's own clipping box (#236). `rail-crawl` is a transform, so it paints after the label beside it;
          without a box of its own its facts slid across the label as they scrolled left. */}
      <div className="min-w-0 flex-1 overflow-hidden">
        <div className="flex w-max rail-crawl">
          <FactRow facts={facts} />
          {/* Second copy makes the -50% loop seamless; it is decorative. */}
          <FactRow facts={facts} aria-hidden />
        </div>
      </div>
    </div>
  );
}

function FactRow({ facts, ...rest }: { facts: TickerFact[] } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className="flex shrink-0 items-center gap-7 whitespace-nowrap px-5 text-[12px] font-semibold uppercase tracking-[0.1em] text-rail-300"
      {...rest}
    >
      {facts.map((fact, i) => (
        <Fragment key={fact.id}>
          {i > 0 ? <span className="text-white/20">·</span> : null}
          <span>
            {fact.label ? <span className="mr-1.5 text-rail-300/70">{fact.label}</span> : null}
            <span className={fact.self ? 'num font-bold text-signal' : 'num text-white/80'}>
              {fact.value}
            </span>
          </span>
        </Fragment>
      ))}
    </div>
  );
}
